import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CalendarDays, Phone, User, X } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";

import { API } from "@/lib/api";

const PERIODOS = [
  { id: "hoje", label: "Hoje" },
  { id: "semana", label: "Semana" },
  { id: "mes", label: "Mês" },
  { id: "todos", label: "Todos" },
  { id: "custom", label: "Personalizado" },
];

const formatYMD = (d) => format(d, "yyyy-MM-dd");

const computeRange = (periodo, customStart, customEnd) => {
  const today = new Date();
  if (periodo === "hoje") {
    const d = formatYMD(today);
    return { data_inicio: d, data_fim: d };
  }
  if (periodo === "semana") {
    return {
      data_inicio: formatYMD(startOfWeek(today, { weekStartsOn: 1 })),
      data_fim: formatYMD(endOfWeek(today, { weekStartsOn: 1 })),
    };
  }
  if (periodo === "mes") {
    return {
      data_inicio: formatYMD(startOfMonth(today)),
      data_fim: formatYMD(endOfMonth(today)),
    };
  }
  if (periodo === "custom") {
    return {
      data_inicio: customStart || "",
      data_fim: customEnd || "",
    };
  }
  return { data_inicio: "", data_fim: "" };
};

const rangeLabel = (periodo, customStart, customEnd) => {
  const today = new Date();
  if (periodo === "hoje") return format(today, "dd 'de' MMMM, yyyy", { locale: ptBR });
  if (periodo === "semana") {
    const s = startOfWeek(today, { weekStartsOn: 1 });
    const e = endOfWeek(today, { weekStartsOn: 1 });
    return `${format(s, "dd/MM", { locale: ptBR })} — ${format(e, "dd/MM", { locale: ptBR })}`;
  }
  if (periodo === "mes") return format(today, "MMMM 'de' yyyy", { locale: ptBR });
  if (periodo === "custom") {
    if (customStart && customEnd) return `${customStart} — ${customEnd}`;
    if (customStart) return `a partir de ${customStart}`;
    if (customEnd) return `até ${customEnd}`;
    return "Selecione o período";
  }
  return "Todos os agendamentos";
};

export function AgendaComercial() {
  const [items, setItems] = useState([]);
  const [periodo, setPeriodo] = useState("semana");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [pipelineId, setPipelineId] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [pipelines, setPipelines] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);

  // load filter options once
  useEffect(() => {
    axios.get(`${API}/pipelines`).then((r) => setPipelines(r.data)).catch(() => {});
    axios.get(`${API}/responsaveis`).then((r) => setResponsaveis(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAgenda();
  }, [periodo, customStart, customEnd, pipelineId, responsavel]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAgenda = async () => {
    try {
      const params = new URLSearchParams();
      const { data_inicio, data_fim } = computeRange(periodo, customStart, customEnd);
      if (data_inicio) params.append("data_inicio", data_inicio);
      if (data_fim) params.append("data_fim", data_fim);
      if (pipelineId) params.append("pipeline_id", pipelineId);
      if (responsavel) params.append("responsavel", responsavel);
      const res = await axios.get(`${API}/agenda?${params}`);
      setItems(res.data);
    } catch {
      setItems([]);
    }
  };

  const clearFilters = () => {
    setPeriodo("semana");
    setCustomStart("");
    setCustomEnd("");
    setPipelineId("");
    setResponsavel("");
  };

  const formatDate = (dateStr) => {
    if (dateStr === "sem-data") return "Sem data definida";
    try {
      const d = new Date(dateStr + "T12:00:00");
      return format(d, "dd/MM (EEE)", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const totalValor = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.agendado_valor) || 0), 0),
    [items]
  );

  const grouped = {};
  items.forEach((item) => {
    const key = item.agendado_data;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  const sortedDates = Object.keys(grouped).sort();

  const hasActiveFilter = pipelineId || responsavel || periodo !== "todos";

  return (
    <div className="h-full overflow-y-auto px-6 py-6" data-testid="agenda-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-zinc-50">Agenda Comercial</h2>
          <p className="text-xs text-zinc-500 mt-0.5" data-testid="agenda-summary">
            {items.length} agendamento{items.length !== 1 ? "s" : ""}
            {totalValor > 0 && (
              <span className="ml-2 text-green-400 font-medium">
                · R${totalValor.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}
              </span>
            )}
          </p>
        </div>
        <span className="text-xs text-zinc-500" data-testid="agenda-range-label">
          {rangeLabel(periodo, customStart, customEnd)}
        </span>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {/* Period chips */}
        <div className="flex bg-zinc-800/60 rounded-md p-0.5" data-testid="agenda-periodo">
          {PERIODOS.map((p) => (
            <button
              key={p.id}
              data-testid={`agenda-periodo-${p.id}`}
              onClick={() => setPeriodo(p.id)}
              className={`px-3 py-1 text-xs rounded transition-all ${
                periodo === p.id
                  ? "bg-zinc-700 text-zinc-100"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date inputs */}
        {periodo === "custom" && (
          <div className="flex items-center gap-2" data-testid="agenda-custom-range">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="agenda-data-inicio"
              className="h-8 px-2 text-xs rounded-md bg-zinc-800/60 border border-zinc-800 text-zinc-200 outline-none focus:border-amber-500/40 transition-colors"
            />
            <span className="text-xs text-zinc-600">até</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="agenda-data-fim"
              className="h-8 px-2 text-xs rounded-md bg-zinc-800/60 border border-zinc-800 text-zinc-200 outline-none focus:border-amber-500/40 transition-colors"
            />
          </div>
        )}

        {/* Campanha select */}
        <select
          value={pipelineId}
          onChange={(e) => setPipelineId(e.target.value)}
          data-testid="agenda-filtro-campanha"
          className="h-8 px-2 text-xs rounded-md bg-zinc-800/60 border border-zinc-800 text-zinc-200 outline-none focus:border-amber-500/40 transition-colors max-w-[200px]"
        >
          <option value="">Todas as campanhas</option>
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>{p.nome}</option>
          ))}
        </select>

        {/* Responsavel select */}
        <select
          value={responsavel}
          onChange={(e) => setResponsavel(e.target.value)}
          data-testid="agenda-filtro-responsavel"
          className="h-8 px-2 text-xs rounded-md bg-zinc-800/60 border border-zinc-800 text-zinc-200 outline-none focus:border-amber-500/40 transition-colors"
        >
          <option value="">Todos responsáveis</option>
          {responsaveis.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        {/* Clear filters */}
        {hasActiveFilter && (
          <button
            onClick={clearFilters}
            data-testid="agenda-limpar-filtros"
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-zinc-500 hover:text-zinc-200 transition-colors"
            title="Limpar filtros"
          >
            <X className="h-3 w-3" /> Limpar
          </button>
        )}
      </div>

      {/* Content */}
      {sortedDates.length === 0 ? (
        <div className="text-center py-20" data-testid="agenda-empty">
          <CalendarDays className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-600">Nenhum agendamento neste período</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((dateKey) => (
            <div key={dateKey}>
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-semibold text-zinc-200">{formatDate(dateKey)}</span>
                <span className="text-xs text-zinc-600">{grouped[dateKey].length} agendamento{grouped[dateKey].length !== 1 ? "s" : ""}</span>
              </div>
              <div className="space-y-2">
                {grouped[dateKey].map((item) => (
                  <div
                    key={item.id}
                    className="bg-[#FAFBFC] rounded-xl p-5 flex items-center justify-between"
                    style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)" }}
                    data-testid={`agenda-item-${item.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm font-medium text-[#111]">{item.nome}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-[#666] flex items-center gap-1">
                            <Phone className="h-3 w-3" />{item.telefone}
                          </span>
                          {item.responsavel && (
                            <span className="text-xs text-[#777] flex items-center gap-1">
                              <User className="h-3 w-3" />{item.responsavel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-0.5 rounded mb-1">{item.campanha}</p>
                      {item.agendado_valor > 0 && (
                        <p className="text-sm font-semibold text-green-600">R${item.agendado_valor}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
